import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/layout/Layout';
import AdminLayout from './pages/admin/AdminLayout';

// Public Pages
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductPage from './pages/ProductPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import NotFoundPage from './pages/NotFoundPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import CollectionsPage from './pages/CollectionsPage';
import WishlistPage from './pages/WishlistPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ShippingPolicyPage from './pages/ShippingPolicyPage';
import ReturnsPage from './pages/ReturnsPage';
import ScrollToTop from './components/layout/ScrollToTop';

// Protected Pages
import AccountPage from './pages/AccountPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderDetailPage from './pages/OrderDetailPage';

// Admin Pages
import DashboardPage from './pages/admin/DashboardPage';
import ContactMessagesAdminPage from './pages/admin/ContactMessagesAdminPage';
import ProductsAdminPage from './pages/admin/ProductsAdminPage';
import OrdersAdminPage from './pages/admin/OrdersAdminPage';
import CustomersAdminPage from './pages/admin/CustomersAdminPage';
import InventoryAdminPage from './pages/admin/InventoryAdminPage';
import DiscountsAdminPage from './pages/admin/DiscountsAdminPage';
import ShippingAdminPage from './pages/admin/ShippingAdminPage';
import ReviewsAdminPage from './pages/admin/ReviewsAdminPage';
import NewsletterAdminPage from './pages/admin/NewsletterAdminPage';
import SettingsAdminPage from './pages/admin/SettingsAdminPage';
import ActivityLogsPage from './pages/admin/ActivityLogsPage';
import CategoriesAdminPage from './pages/admin/CategoriesAdminPage';
import PaymentsAdminPage from './pages/admin/PaymentsAdminPage';
import StaffAdminPage from './pages/admin/StaffAdminPage';
import SupportChatAdminPage from './pages/admin/SupportChatAdminPage';
import FAQPage from './pages/FAQPage';
import ChatWidget from './components/support/ChatWidget';
import ComingSoonPage from './pages/ComingSoonPage';
import { useMaintenanceStore } from './store/maintenanceStore';
import { useEffect } from 'react';
import { useAuthStore } from './store/authStore';

// Keeps the customer chat widget off the admin panel — an admin managing
// the store doesn't need their own storefront support widget in the way.
const ChatWidgetGate: React.FC = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  return <ChatWidget />;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

// Dashboard-only landing guard — any admin-tier account (admin, super_admin,
// or staff) can see the general dashboard stats. Section-level access below
// this is controlled separately via PermissionRoute.
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const role = user?.role;
  if (role !== 'admin' && role !== 'super_admin' && role !== 'staff') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

// Sensitive sections (Customers) that staff accounts should never reach,
// regardless of any permission they've been granted — matches the original
// pre-staff-system behavior exactly.
const FullAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
};

// Section pages gated by a specific permission key. Admin/super_admin
// always pass; staff accounts need that exact permission granted.
const PermissionRoute: React.FC<{ children: React.ReactNode; permission: string }> = ({ children, permission }) => {
  const { user, isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const role = user?.role;
  if (role === 'admin' || role === 'super_admin') return <>{children}</>;
  if (role === 'staff') {
    const perms: string[] = (user as any)?.permissions || [];
    if (perms.includes(permission)) return <>{children}</>;
  }
  return <Navigate to="/admin" replace />;
};

// Strictly super_admin — used only for Staff account management, matching
// the backend's own super_admin-only enforcement for that feature.
const SuperAdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'super_admin') {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
};

const App: React.FC = () => {
  const { isMaintenanceMode, isLoading, checkMaintenance } = useMaintenanceStore();
  const { user } = useAuthStore();

  useEffect(() => {
  checkMaintenance();
  const interval = setInterval(() => {
    checkMaintenance();
  }, 10000); // checks every 10 seconds
  return () => clearInterval(interval);
}, []);

  const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';

  if (isLoading) {
    return null;
  }

  if (isMaintenanceMode && !isAdminUser) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<ComingSoonPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
    <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        <Route path="/shop" element={<Layout><ShopPage /></Layout>} />
        <Route path="/collections" element={<Layout><CollectionsPage /></Layout>} />
        <Route path="/products/:slug" element={<Layout><ProductPage /></Layout>} />
        <Route path="/about" element={<Layout><AboutPage /></Layout>} />
        <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
        <Route path="/faq" element={<Layout><FAQPage /></Layout>} />
        <Route path="/wishlist" element={<Layout><WishlistPage /></Layout>} />
        <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
        <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
        <Route path="/shipping-policy" element={<Layout><ShippingPolicyPage /></Layout>} />
        <Route path="/returns" element={<Layout><ReturnsPage /></Layout>} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/paystack/callback" element={<PaymentSuccessPage />} />
        <Route path="/payment/paypal/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/paypal/cancel" element={<PaymentCancelPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        

        {/* Protected Routes */}
        <Route path="/account" element={
          <ProtectedRoute>
            <Layout><AccountPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/orders/:id" element={
          <ProtectedRoute>
            <Layout><OrderDetailPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/checkout" element={
          <ProtectedRoute>
            <CheckoutPage />
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminLayout><DashboardPage /></AdminLayout>
          </AdminRoute>
        } />
        <Route path="/admin/products" element={
          <PermissionRoute permission="products">
            <AdminLayout><ProductsAdminPage /></AdminLayout>
          </PermissionRoute>
        } />
        <Route path="/admin/orders" element={
          <PermissionRoute permission="orders">
            <AdminLayout><OrdersAdminPage /></AdminLayout>
          </PermissionRoute>
        } />
        <Route path="/admin/customers" element={
          <FullAdminRoute>
            <AdminLayout><CustomersAdminPage /></AdminLayout>
          </FullAdminRoute>
        } />
        <Route path="/admin/categories" element={
          <PermissionRoute permission="categories">
            <AdminLayout><CategoriesAdminPage /></AdminLayout>
          </PermissionRoute>
        } />
        <Route path="/admin/inventory" element={
          <PermissionRoute permission="inventory">
            <AdminLayout><InventoryAdminPage /></AdminLayout>
          </PermissionRoute>
        } />
        <Route path="/admin/discounts" element={
          <PermissionRoute permission="discounts">
            <AdminLayout><DiscountsAdminPage /></AdminLayout>
          </PermissionRoute>
        } />
        <Route path="/admin/shipping" element={
          <PermissionRoute permission="shipping">
            <AdminLayout><ShippingAdminPage /></AdminLayout>
          </PermissionRoute>
        } />
        <Route path="/admin/reviews" element={
          <PermissionRoute permission="reviews">
            <AdminLayout><ReviewsAdminPage /></AdminLayout>
          </PermissionRoute>
        } />
        <Route path="/admin/newsletter" element={
          <PermissionRoute permission="newsletter">
            <AdminLayout><NewsletterAdminPage /></AdminLayout>
          </PermissionRoute>
        } />
        <Route path="/admin/settings" element={
          <FullAdminRoute>
            <AdminLayout><SettingsAdminPage /></AdminLayout>
          </FullAdminRoute>
        } />
        <Route path="/admin/logs" element={
          <FullAdminRoute>
            <AdminLayout><ActivityLogsPage /></AdminLayout>
          </FullAdminRoute>
        } />
        <Route path="/admin/payments" element={
          <PermissionRoute permission="payments">
            <AdminLayout><PaymentsAdminPage /></AdminLayout>
          </PermissionRoute>
        } />
        <Route path="/admin/staff" element={
          <SuperAdminOnlyRoute>
            <AdminLayout><StaffAdminPage /></AdminLayout>
          </SuperAdminOnlyRoute>
        } />
        <Route path="/admin/messages" element={
  <PermissionRoute permission="contact">
    <AdminLayout><ContactMessagesAdminPage /></AdminLayout>
  </PermissionRoute>
} />
        <Route path="/admin/support" element={
          <PermissionRoute permission="support">
            <AdminLayout><SupportChatAdminPage /></AdminLayout>
          </PermissionRoute>
        } />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <ChatWidgetGate />
    </BrowserRouter>
  );
};

export default App;